import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { RiArrowDownSFill } from "react-icons/ri";
import { motion, AnimatePresence } from "framer-motion";
import { GiHamburgerMenu } from "react-icons/gi";
import logo from "../../assets/Frame 411.png";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";
import DownloadModal from "../common/DownloadModal";

const BottomNavbar = () => {
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const navbarRef = useRef(null);
  const subsubRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [activeServiceId, setActiveServiceId] = useState(null);
  const [activeSubserviceId, setActiveSubserviceId] = useState(null);
  const [servicesData, setServicesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subsubPosition, setSubsubPosition] = useState("left-0");
  const [subMenuPositions, setSubMenuPositions] = useState({});

  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState(null);

  const handleOpenDownloadModal = (serviceId) => {
    setSelectedServiceId(serviceId);
    setIsDownloadOpen(true);
  };

  const handleCloseDownloadModal = () => {
    setIsDownloadOpen(false);
    setSelectedServiceId(null);
  };

  useEffect(() => {
    AOS.init({ duration: 1000 });
    getAllServicesData();
    
    const intervalId = setInterval(() => {
      getAllServicesData(false);
    }, 30000);
    
    return () => clearInterval(intervalId);
  }, []);

  const getAllServicesData = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/services/get-all-services-data`
      );
      if (data && Array.isArray(data)) {
        // Sort services by order
        const sortedData = [...data].sort((a, b) => (a.order || 0) - (b.order || 0));
        
        // Also sort subservices within each service by order
        sortedData.forEach(service => {
          if (service.subservices && Array.isArray(service.subservices)) {
            service.subservices.sort((a, b) => (a.order || 0) - (b.order || 0));
          }
        });
        
        setServicesData(sortedData);
      } else {
        console.error("Invalid data format:", data);
        setServicesData([]);
      }
    } catch (error) {
      console.error("Error fetching service data:", error);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        navbarRef.current &&
        !navbarRef.current.contains(e.target)
      ) {
        setActiveServiceId(null);
        setActiveSubserviceId(null);
      }
      if (
        menuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(e.target)
      ) {
        setMenuOpen(false);
        setActiveServiceId(null);
        setActiveSubserviceId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  // Fix main dropdown position
  useEffect(() => {
    if (subsubRef.current) {
      const rect = subsubRef.current.getBoundingClientRect();
      if (rect.right > window.innerWidth - 50) {
        setSubsubPosition("right-0");
      } else {
        setSubsubPosition("left-0");
      }
    }
  }, [activeServiceId]);

  // Function to calculate and store submenu position
  const handleSubmenuOpen = (subId, element) => {
    if (!element) return;
    const rect = element.getBoundingClientRect();
    const menuWidth = 220;
    const spaceRight = window.innerWidth - rect.right;
    const shouldOpenLeft = spaceRight < menuWidth + 10;
    
    setSubMenuPositions(prev => ({
      ...prev,
      [subId]: shouldOpenLeft ? "left" : "right"
    }));
  };

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const handleServiceClick = (serviceId) => {
    setActiveSubserviceId(null);
    setActiveServiceId((prev) => (prev === serviceId ? null : serviceId));
  };

  const handleSubserviceClick = (subId, event) => {
    if (event?.currentTarget) {
      handleSubmenuOpen(subId, event.currentTarget);
    }
    setActiveSubserviceId((prev) => (prev === subId ? null : subId));
  };

  // ✅ CORRECTED: Navigation with SEO-friendly URLs
  const handleItemClick = (type, slug, id, serviceSlug) => {
    if (type === "service") {
      navigate(`/services/${slug || id}`);
    } else if (type === "subservice") {
      // ✅ USE THE NEW SEO-FRIENDLY URL
      const path = slug || id;
      navigate(`/sub-service/${path}`);
    } else if (type === "subsubservice") {
      navigate(`/sub-subservice/${slug || id}`);
    }
    setActiveServiceId(null);
    setActiveSubserviceId(null);
    setMenuOpen(false);
  };

  // ✅ For "View All Services" or similar buttons
  const handleViewAllServices = () => {
    navigate('/our-service');
    setActiveServiceId(null);
    setActiveSubserviceId(null);
    setMenuOpen(false);
  };

  const SkeletonLoader = () => (
    <div className="w-full bg-white sticky top-0 z-[999] shadow-md">
      <div className="bg-[#101828] px-5 py-3 2xl:flex hidden items-center justify-center gap-6">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((item) => (
          <div key={item} className="animate-pulse">
            <div className="h-6 bg-gray-700 rounded w-32"></div>
          </div>
        ))}
      </div>
      <div className="bg-white px-4 py-2 2xl:hidden flex items-center justify-between shadow-md">
        <div className="animate-pulse">
          <div className="h-12 w-16 bg-gray-300 rounded"></div>
        </div>
        <div className="animate-pulse">
          <div className="h-8 w-8 bg-gray-400 rounded"></div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <SkeletonLoader />;
  }

  return (
    <div ref={navbarRef} className="w-full bg-white shadow-md">
      {/* DESKTOP NAVBAR */}
      <div className="bg-[#101828] px-5 py-3 2xl:flex hidden items-center justify-center gap-6 relative z-[999]">
        {servicesData.map((service) => (
          <div key={service.id || service._id} className="relative text-white" ref={menuRef}>
            <div
              className={`flex items-center gap-2 cursor-pointer font-medium transition-colors duration-200 ${
                activeServiceId === (service.id || service._id) 
                  ? "text-yellow-400" 
                  : "hover:text-yellow-400"
              }`}
              onClick={() =>
                service.subservices?.length > 0
                  ? handleServiceClick(service.id || service._id)
                  : handleItemClick("service", service.slug || service.id, service.id || service._id)
              }
            >
              {service.serviceName}
              {service.subservices?.length > 0 && <RiArrowDownSFill size={16} />}
            </div>

            <AnimatePresence>
              {activeServiceId === (service.id || service._id) && service.subservices?.length > 0 && (
                <motion.div
                  ref={subsubRef}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                  className={`absolute top-full mt-2 bg-white text-black shadow-2xl rounded-md w-[320px] ${subsubPosition}`}
                  style={{ zIndex: 9999 }}
                >
                  <div className="p-4 bg-white">
                    {service.subservices.map((sub) => (
                      <div key={sub._id} className="mb-2">
                        <div
                          className={`flex justify-between items-center cursor-pointer py-2 px-2 rounded-md font-medium transition ${
                            activeSubserviceId === sub._id
                              ? "bg-gray-100 text-blue-600"
                              : "hover:bg-gray-50"
                          }`}
                          onMouseEnter={(e) => {
                            handleSubmenuOpen(sub._id, e.currentTarget);
                            setActiveSubserviceId(sub._id);
                          }}
                          onClick={(e) => {
                            if (!sub.subsubservices?.length) {
                              // ✅ Use SEO-friendly URL
                              handleItemClick("subservice", sub.slug || sub._id, sub._id, service.slug);
                            } else {
                              if (activeSubserviceId !== sub._id) {
                                handleSubmenuOpen(sub._id, e.currentTarget);
                              }
                              setActiveSubserviceId(
                                activeSubserviceId === sub._id ? null : sub._id
                              );
                            }
                          }}
                        >
                          {sub.subserviceName}
                          {sub.subsubservices?.length > 0 && (
                            <RiArrowDownSFill
                              className={`transition-transform ${
                                activeSubserviceId === sub._id ? "rotate-180" : ""
                              }`}
                              size={14}
                            />
                          )}
                        </div>

                        <AnimatePresence>
                          {activeSubserviceId === sub._id && sub.subsubservices?.length > 0 && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className={`absolute top-2 bg-white text-black shadow-lg rounded-md min-w-[220px] z-[999] ${
                                subMenuPositions[sub._id] === "left"
                                  ? "right-full mr-2"
                                  : "left-full ml-2"
                              }`}
                            >
                              {sub.subsubservices.map((subsub) => (
                                <div
                                  key={subsub._id}
                                  className="px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                                  onClick={() =>
                                    handleItemClick("subsubservice", subsub.slug || subsub._id, subsub._id)
                                  }
                                >
                                  {subsub.subsubserviceName}
                                </div>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDownloadModal(service.id || service._id);
                      }}
                      className="mt-3 bg-yellow-400 text-black py-2 px-4 rounded-md hover:bg-yellow-500 transition w-full font-medium"
                    >
                      Download
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>

      {/* MOBILE NAVBAR */}
      <div className="bg-white px-4 py-2 2xl:hidden flex items-center justify-between shadow-md relative z-[9999]">
        <Link to="/">
          <img
            data-aos="zoom-in-left"
            src={logo}
            alt="LOGO"
            className="h-auto w-16 cursor-pointer"
          />
        </Link>
        <GiHamburgerMenu size={28} onClick={toggleMenu} className="cursor-pointer" />
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-[9999]"
            onClick={() => setMenuOpen(false)}
          >
            <motion.div
              ref={mobileMenuRef}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="bg-white w-3/4 max-w-sm h-full overflow-y-auto shadow-lg p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-lg font-bold mb-4">Menu</h2>
              {servicesData.map((service) => (
                <div key={service.id || service._id} className="mb-3">
                  <div
                    className="flex justify-between items-center py-2 cursor-pointer border-b"
                    onClick={() =>
                      service.subservices?.length > 0
                        ? handleServiceClick(service.id || service._id)
                        : handleItemClick("service", service.slug || service.id, service.id || service._id)
                    }
                  >
                    <span className="font-medium">{service.serviceName}</span>
                    {service.subservices?.length > 0 && <RiArrowDownSFill size={20} />}
                  </div>
                  <AnimatePresence>
                    {activeServiceId === (service.id || service._id) && service.subservices?.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="pl-4 border-l border-gray-200 space-y-2"
                      >
                        {service.subservices.map((sub) => (
                          <div key={sub._id}>
                            <div
                              className="py-2 cursor-pointer hover:text-blue-600 flex justify-between items-center"
                              onClick={() =>
                                sub.subsubservices?.length > 0
                                  ? handleSubserviceClick(sub._id)
                                  : handleItemClick("subservice", sub.slug || sub._id, sub._id, service.slug)
                              }
                            >
                              <span className="font-medium">{sub.subserviceName}</span>
                              {sub.subsubservices?.length > 0 && <RiArrowDownSFill size={16} />}
                            </div>
                            <AnimatePresence>
                              {activeSubserviceId === sub._id && sub.subsubservices?.length > 0 && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="pl-4 border-l border-gray-300 space-y-1"
                                >
                                  {sub.subsubservices.map((subsub) => (
                                    <div
                                      key={subsub._id}
                                      className="py-2 text-sm cursor-pointer hover:text-blue-600"
                                      onClick={() =>
                                        handleItemClick("subsubservice", subsub.slug || subsub._id, subsub._id)
                                      }
                                    >
                                      {subsub.subsubserviceName}
                                    </div>
                                  ))}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        ))}
                        <div className="pt-3 border-t border-gray-200">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDownloadModal(service.id || service._id);
                            }}
                            className="mt-3 bg-yellow-400 text-black py-2 px-4 rounded-md hover:bg-yellow-500 transition w-full font-medium"
                          >
                            Download
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate("/login");
                }}
                className="mt-6 bg-yellow-400 text-black font-semibold py-2 w-full rounded-md hover:bg-yellow-500 transition"
              >
                Login
              </button>
              <button
                className="mt-3 bg-gray-500 text-white py-2 w-full rounded-md"
                onClick={() => setMenuOpen(false)}
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <DownloadModal
        isOpen={isDownloadOpen}
        onClose={handleCloseDownloadModal}
        serviceId={selectedServiceId}
      />
    </div>
  );
};

export default BottomNavbar;


// import React, { useEffect, useRef, useState } from "react";
// import { Link, useNavigate } from "react-router-dom";
// import { RiArrowDownSFill } from "react-icons/ri";
// import { motion, AnimatePresence } from "framer-motion";
// import { GiHamburgerMenu } from "react-icons/gi";
// import logo from "../../assets/Frame 411.png";
// import axios from "axios";
// import AOS from "aos";
// import "aos/dist/aos.css";
// import DownloadModal from "../common/DownloadModal";

// const BottomNavbar = () => {
//   const navigate = useNavigate();
//   const menuRef = useRef(null);
//   const navbarRef = useRef(null);
//   const subsubRef = useRef(null);
//   const mobileMenuRef = useRef(null);

//   const [menuOpen, setMenuOpen] = useState(false);
//   const [activeServiceId, setActiveServiceId] = useState(null);
//   const [activeSubserviceId, setActiveSubserviceId] = useState(null);
//   const [servicesData, setServicesData] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [subsubPosition, setSubsubPosition] = useState("left-0");
  
//   // NEW: state for each submenu's position (left or right)
//   const [subMenuPositions, setSubMenuPositions] = useState({});

//   const [isDownloadOpen, setIsDownloadOpen] = useState(false);
//   const [selectedServiceId, setSelectedServiceId] = useState(null);

//   const handleOpenDownloadModal = (serviceId) => {
//     setSelectedServiceId(serviceId);
//     setIsDownloadOpen(true);
//   };

//   const handleCloseDownloadModal = () => {
//     setIsDownloadOpen(false);
//     setSelectedServiceId(null);
//   };

//   useEffect(() => {
//     AOS.init({ duration: 1000 });
//     getAllServicesData();
    
//     const intervalId = setInterval(() => {
//       getAllServicesData(false);
//     }, 30000);
    
//     return () => clearInterval(intervalId);
//   }, []);

//   const getAllServicesData = async (showLoading = true) => {
//     try {
//       if (showLoading) setLoading(true);
//       const { data } = await axios.get(
//         `${process.env.REACT_APP_API_URL}/services/get-all-services-data`
//       );
//       if (data && Array.isArray(data)) {
//         const sortedData = [...data].sort((a, b) => a.order - b.order);
//         setServicesData(sortedData);
//       } else {
//         console.error("Invalid data format:", data);
//         setServicesData([]);
//       }
//     } catch (error) {
//       console.error("Error fetching service data:", error);
//     } finally {
//       if (showLoading) setLoading(false);
//     }
//   };

//   // Close dropdowns when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (e) => {
//       if (
//         menuRef.current &&
//         !menuRef.current.contains(e.target) &&
//         navbarRef.current &&
//         !navbarRef.current.contains(e.target)
//       ) {
//         setActiveServiceId(null);
//         setActiveSubserviceId(null);
//       }
//       if (
//         menuOpen &&
//         mobileMenuRef.current &&
//         !mobileMenuRef.current.contains(e.target)
//       ) {
//         setMenuOpen(false);
//         setActiveServiceId(null);
//         setActiveSubserviceId(null);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, [menuOpen]);

//   // Fix main dropdown position (already working)
//   useEffect(() => {
//     if (subsubRef.current) {
//       const rect = subsubRef.current.getBoundingClientRect();
//       if (rect.right > window.innerWidth - 50) {
//         setSubsubPosition("right-0");
//       } else {
//         setSubsubPosition("left-0");
//       }
//     }
//   }, [activeServiceId]);

//   // NEW: Function to calculate and store submenu position when it opens
//   const handleSubmenuOpen = (subId, element) => {
//     if (!element) return;
//     const rect = element.getBoundingClientRect();
//     const menuWidth = 220; // approximate width of sub-submenu
//     const spaceRight = window.innerWidth - rect.right;
//     const shouldOpenLeft = spaceRight < menuWidth + 10;
    
//     setSubMenuPositions(prev => ({
//       ...prev,
//       [subId]: shouldOpenLeft ? "left" : "right"
//     }));
//   };

//   const toggleMenu = () => setMenuOpen(!menuOpen);

//   const handleServiceClick = (serviceId) => {
//     setActiveSubserviceId(null);
//     setActiveServiceId((prev) => (prev === serviceId ? null : serviceId));
//   };

//   const handleSubserviceClick = (subId, event) => {
//     // When opening the submenu, calculate its position
//     if (event?.currentTarget) {
//       handleSubmenuOpen(subId, event.currentTarget);
//     }
//     setActiveSubserviceId((prev) => (prev === subId ? null : subId));
//   };

//   const handleItemClick = (type, id) => {
//     if (type === "service") navigate(`/service-details/${id}`);
//     else if (type === "subservice") navigate(`/sub-service-details/${id}`);
//     else if (type === "subsubservice") navigate(`/sub-subservice-details/${id}`);
//     setActiveServiceId(null);
//     setActiveSubserviceId(null);
//     setMenuOpen(false);
//   };

//   const SkeletonLoader = () => (
//     <div className="w-full bg-white sticky top-0 z-[999] shadow-md">
//       <div className="bg-[#101828] px-5 py-3 2xl:flex hidden items-center justify-center gap-6">
//         {[1, 2, 3, 4, 5, 6, 7, 8, 9,10].map((item) => (
//           <div key={item} className="animate-pulse">
//             <div className="h-6 bg-gray-700 rounded w-32"></div>
//           </div>
//         ))}
//       </div>
//       <div className="bg-white px-4 py-2 2xl:hidden flex items-center justify-between shadow-md">
//         <div className="animate-pulse">
//           <div className="h-12 w-16 bg-gray-300 rounded"></div>
//         </div>
//         <div className="animate-pulse">
//           <div className="h-8 w-8 bg-gray-400 rounded"></div>
//         </div>
//       </div>
//     </div>
//   );

//   if (loading) {
//     return <SkeletonLoader />;
//   }

//   return (
//     <div ref={navbarRef} className="w-full bg-white shadow-md">
//       {/* DESKTOP NAVBAR */}
//       <div className="bg-[#101828] px-5 py-3 2xl:flex hidden items-center justify-center gap-6 relative z-[999]">
//         {servicesData.map((service) => (
//           <div key={service.id || service._id} className="relative text-white" ref={menuRef}>
//             <div
//               className={`flex items-center gap-2 cursor-pointer font-medium transition-colors duration-200 ${
//                 activeServiceId === (service.id || service._id) 
//                   ? "text-yellow-400" 
//                   : "hover:text-yellow-400"
//               }`}
//               onClick={() =>
//                 service.subservices?.length > 0
//                   ? handleServiceClick(service.id || service._id)
//                   : handleItemClick("service", service.id || service._id)
//               }
//             >
//               {service.serviceName}
//               {service.subservices?.length > 0 && <RiArrowDownSFill size={16} />}
//             </div>

//             <AnimatePresence>
//               {activeServiceId === (service.id || service._id) && service.subservices?.length > 0 && (
//                 <motion.div
//                   ref={subsubRef}
//                   initial={{ opacity: 0, y: 10 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   exit={{ opacity: 0, y: 10 }}
//                   transition={{ duration: 0.2 }}
//                   className={`absolute top-full mt-2 bg-white text-black shadow-2xl rounded-md w-[320px] ${subsubPosition}`}
//                   style={{ zIndex: 9999 }}
//                 >
//                   <div className="p-4 bg-white">
//                     {service.subservices.map((sub) => (
//                       <div key={sub._id} className="mb-2">
//                         <div
//                           className={`flex justify-between items-center cursor-pointer py-2 px-2 rounded-md font-medium transition ${
//                             activeSubserviceId === sub._id
//                               ? "bg-gray-100 text-blue-600"
//                               : "hover:bg-gray-50"
//                           }`}
//                           onMouseEnter={(e) => {
//                             // Calculate position when hover opens submenu
//                             handleSubmenuOpen(sub._id, e.currentTarget);
//                             setActiveSubserviceId(sub._id);
//                           }}
//                           onClick={(e) => {
//                             if (!sub.subsubservices?.length) {
//                               handleItemClick("subservice", sub._id);
//                             } else {
//                               // Toggle submenu
//                               if (activeSubserviceId !== sub._id) {
//                                 handleSubmenuOpen(sub._id, e.currentTarget);
//                               }
//                               setActiveSubserviceId(
//                                 activeSubserviceId === sub._id ? null : sub._id
//                               );
//                             }
//                           }}
//                         >
//                           {sub.subserviceName}
//                           {sub.subsubservices?.length > 0 && (
//                             <RiArrowDownSFill
//                               className={`transition-transform ${
//                                 activeSubserviceId === sub._id ? "rotate-180" : ""
//                               }`}
//                               size={14}
//                             />
//                           )}
//                         </div>

//                         <AnimatePresence>
//                           {activeSubserviceId === sub._id && sub.subsubservices?.length > 0 && (
//                             <motion.div
//                               initial={{ height: 0, opacity: 0 }}
//                               animate={{ height: "auto", opacity: 1 }}
//                               exit={{ height: 0, opacity: 0 }}
//                               // FIX: dynamic positioning based on stored value
//                               className={`absolute top-2 bg-white text-black shadow-lg rounded-md min-w-[220px] z-[999] ${
//                                 subMenuPositions[sub._id] === "left"
//                                   ? "right-full mr-2"
//                                   : "left-full ml-2"
//                               }`}
//                             >
//                               {sub.subsubservices.map((subsub) => (
//                                 <div
//                                   key={subsub._id}
//                                   className="px-4 py-2 text-sm cursor-pointer hover:bg-gray-100 whitespace-nowrap"
//                                   onClick={() =>
//                                     handleItemClick("subsubservice", subsub._id)
//                                   }
//                                 >
//                                   {subsub.subsubserviceName}
//                                 </div>
//                               ))}
//                             </motion.div>
//                           )}
//                         </AnimatePresence>
//                       </div>
//                     ))}

//                     <button
//                       onClick={(e) => {
//                         e.stopPropagation();
//                         handleOpenDownloadModal(service.id || service._id);
//                       }}
//                       className="mt-3 bg-yellow-400 text-black py-2 px-4 rounded-md hover:bg-yellow-500 transition w-full font-medium"
//                     >
//                       Download
//                     </button>
//                   </div>
//                 </motion.div>
//               )}
//             </AnimatePresence>
//           </div>
//         ))}
//       </div>

//       {/* MOBILE NAVBAR (unchanged, works fine) */}
//       <div className="bg-white px-4 py-2 2xl:hidden flex items-center justify-between shadow-md relative z-[9999]">
//         <Link to="/">
//           <img
//             data-aos="zoom-in-left"
//             src={logo}
//             alt="LOGO"
//             className="h-auto w-16 cursor-pointer"
//           />
//         </Link>
//         <GiHamburgerMenu size={28} onClick={toggleMenu} className="cursor-pointer" />
//       </div>

//       <AnimatePresence>
//         {menuOpen && (
//           <motion.div
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             exit={{ opacity: 0 }}
//             className="fixed inset-0 bg-black bg-opacity-50 flex justify-end z-[9999]"
//             onClick={() => setMenuOpen(false)}
//           >
//             <motion.div
//               ref={mobileMenuRef}
//               initial={{ x: "100%" }}
//               animate={{ x: 0 }}
//               exit={{ x: "100%" }}
//               transition={{ type: "tween", duration: 0.3 }}
//               className="bg-white w-3/4 max-w-sm h-full overflow-y-auto shadow-lg p-5"
//               onClick={(e) => e.stopPropagation()}
//             >
//               <h2 className="text-lg font-bold mb-4">Menu</h2>
//               {servicesData.map((service) => (
//                 <div key={service.id || service._id} className="mb-3">
//                   <div
//                     className="flex justify-between items-center py-2 cursor-pointer border-b"
//                     onClick={() =>
//                       service.subservices?.length > 0
//                         ? handleServiceClick(service.id || service._id)
//                         : handleItemClick("service", service.id || service._id)
//                     }
//                   >
//                     <span className="font-medium">{service.serviceName}</span>
//                     {service.subservices?.length > 0 && <RiArrowDownSFill size={20} />}
//                   </div>
//                   <AnimatePresence>
//                     {activeServiceId === (service.id || service._id) && service.subservices?.length > 0 && (
//                       <motion.div
//                         initial={{ opacity: 0, height: 0 }}
//                         animate={{ opacity: 1, height: "auto" }}
//                         exit={{ opacity: 0, height: 0 }}
//                         className="pl-4 border-l border-gray-200 space-y-2"
//                       >
//                         {service.subservices.map((sub) => (
//                           <div key={sub._id}>
//                             <div
//                               className="py-2 cursor-pointer hover:text-blue-600 flex justify-between items-center"
//                               onClick={() =>
//                                 sub.subsubservices?.length > 0
//                                   ? handleSubserviceClick(sub._id)
//                                   : handleItemClick("subservice", sub._id)
//                               }
//                             >
//                               <span className="font-medium">{sub.subserviceName}</span>
//                               {sub.subsubservices?.length > 0 && <RiArrowDownSFill size={16} />}
//                             </div>
//                             <AnimatePresence>
//                               {activeSubserviceId === sub._id && sub.subsubservices?.length > 0 && (
//                                 <motion.div
//                                   initial={{ opacity: 0, height: 0 }}
//                                   animate={{ opacity: 1, height: "auto" }}
//                                   exit={{ opacity: 0, height: 0 }}
//                                   className="pl-4 border-l border-gray-300 space-y-1"
//                                 >
//                                   {sub.subsubservices.map((subsub) => (
//                                     <div
//                                       key={subsub._id}
//                                       className="py-2 text-sm cursor-pointer hover:text-blue-600"
//                                       onClick={() =>
//                                         handleItemClick("subsubservice", subsub._id)
//                                       }
//                                     >
//                                       {subsub.subsubserviceName}
//                                     </div>
//                                   ))}
//                                 </motion.div>
//                               )}
//                             </AnimatePresence>
//                           </div>
//                         ))}
//                         <div className="pt-3 border-t border-gray-200">
//                           <button
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               handleOpenDownloadModal(service.id || service._id);
//                             }}
//                             className="mt-3 bg-yellow-400 text-black py-2 px-4 rounded-md hover:bg-yellow-500 transition w-full font-medium"
//                           >
//                             Download
//                           </button>
//                         </div>
//                       </motion.div>
//                     )}
//                   </AnimatePresence>
//                 </div>
//               ))}
//               <button
//                 onClick={() => {
//                   setMenuOpen(false);
//                   navigate("/login");
//                 }}
//                 className="mt-6 bg-yellow-400 text-black font-semibold py-2 w-full rounded-md hover:bg-yellow-500 transition"
//               >
//                 Login
//               </button>
//               <button
//                 className="mt-3 bg-gray-500 text-white py-2 w-full rounded-md"
//                 onClick={() => setMenuOpen(false)}
//               >
//                 Close
//               </button>
//             </motion.div>
//           </motion.div>
//         )}
//       </AnimatePresence>

//       <DownloadModal
//         isOpen={isDownloadOpen}
//         onClose={handleCloseDownloadModal}
//         serviceId={selectedServiceId}
//       />
//     </div>
//   );
// };

// export default BottomNavbar;